import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CircleNotch,
  Warning,
  CaretRight,
  Table as TableIcon,
  Code,
} from "@phosphor-icons/react";
import { API, http, getWithRetry } from "@/lib/constants";
import { cn, formatLovelace, truncMid } from "@/lib/format";
import { tabsFor, columnsFor, identifierMatchesMode } from "@/lib/tabs";
import { Badge, CopyBtn, Mono, Tile } from "@/components/primitives";
import { Controls } from "@/components/Controls";
import { Table } from "@/components/Table";
import { JsonView } from "@/components/JsonView";
import { AddressTransactionsList } from "@/components/tabs/AddressTransactionsList";
import { AddressTotalSnapshot } from "@/components/tabs/AddressTotalSnapshot";
import { BlockListCards } from "@/components/tabs/BlockListCards";
import { PoolHistoryTable } from "@/components/tabs/PoolHistoryTable";
import { TxHashesList } from "@/components/tx/TxHashesList";
import { FromToList } from "@/components/tx/FromToList";
import { TxFlowDiagram } from "@/components/tx/TxFlowDiagram";

// ---------- tab content panels (one per endpoint) ----------
export const DataTab = ({ mode, tab, identifier, amountMode, onPickTx, onPickBlock, stakeAddress }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("table"); // table | json
  const [page, setPage] = useState(1);
  const cfg = tabsFor(mode.key).find((t) => t.key === tab);
  const shape = cfg?.shape || "list"; // list | object | total | hashes | address_transactions | wallet_addresses
  const colKey = cfg?.colKey || tab;
  // The upstream pool epoch-history endpoint times out (503/504) on large page
  // sizes for high-stake pools, so keep its page size small.
  const isPoolHistory = colKey === "pool_history";
  const [count, setCount] = useState(isPoolHistory ? 10 : 25);
  // Chronological / sequential lists default to newest-first (desc) for a
  // consistent explorer feel. Purely non-time lists (token holders, wallet
  // addresses, policy assets, affected addresses) stay asc.
  const descByDefault =
    shape === "hashes" ||
    shape === "address_transactions" ||
    ["history", "withdrawals", "rewards", "transactions"].includes(tab) ||
    ["asset_history", "asset_transactions", "block_txs", "block_list", "pool_history", "metadata_label_entries"].includes(colKey);
  const [order, setOrder] = useState(descByDefault ? "desc" : "asc");
  const [viewAll, setViewAll] = useState(false);
  const [accumulated, setAccumulated] = useState([]);
  const [walletTotal, setWalletTotal] = useState(null);
  const abortRef = useRef(null);

  const isPaginated =
    shape === "list" || shape === "hashes" || shape === "address_transactions" || shape === "wallet_addresses";

  // Normalize the API response to a row array (handles `hashes` shape).
  const normalizeRows = useCallback(
    (raw) => {
      if (!Array.isArray(raw)) return [];
      if (shape === "hashes") return raw.map((v) => ({ value: v }));
      return raw;
    },
    [shape],
  );

  const fetchData = useCallback(async () => {
    // Cancel any in-flight request for the previous scope so a stale response
    // can't overwrite the current one — and, critically, so React.StrictMode's
    // double-mount doesn't fire two live requests against the ~10 req/window
    // upstream rate limit.
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const signal = controller.signal;
    // Normalize a caught error into a shape the error UI understands, flagging
    // transient (retryable) conditions off the HTTP status / missing response
    // rather than string-matching the message.
    const toError = (e) => {
      const status = e.response?.status;
      const transient = !e.response || [429, 500, 502, 503, 504].includes(status);
      const base = e.response?.data?.detail ?? e.response?.data;
      if (base && typeof base === "object") {
        return { ...base, status_code: base.status_code ?? status, _transient: transient };
      }
      return {
        status_code: status,
        message: typeof base === "string" ? base : e.message,
        _transient: transient,
      };
    };
    if (shape === "wallet_addresses") {
      if (!stakeAddress) {
        setData(null);
        setError(null);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const params = { page, count, order };
        const res = await getWithRetry(`${API}/accounts/${stakeAddress}/addresses`, { params }, { signal });
        setData(res.data);
        if (viewAll) {
          const incoming = normalizeRows(res.data);
          setAccumulated((prev) => (page === 1 ? incoming : [...prev, ...incoming]));
        }
      } catch (e) {
        if (e.code === "ERR_CANCELED") return;
        setError(toError(e));
      } finally {
        if (!signal.aborted) setLoading(false);
      }
      return;
    }
    if (!identifier || !cfg) return;
    if (!identifierMatchesMode(mode.key, identifier)) {
      setData(null);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = isPaginated ? { page, count, order } : {};
      const url = cfg.pathFn
        ? `${API}${cfg.pathFn(identifier)}`
        : `${API}${mode.basePath}/${identifier}${cfg.path}`;
      const res = await getWithRetry(url, { params }, { signal });
      setData(res.data);
      // Accumulate for "View All" mode. page>1 → append, page===1 → replace.
      if (isPaginated && viewAll) {
        const incoming = normalizeRows(res.data);
        setAccumulated((prev) => (page === 1 ? incoming : [...prev, ...incoming]));
      }
    } catch (e) {
      if (e.code === "ERR_CANCELED") return;
      // A valid-but-unused address 404s on list/detail endpoints — treat as
      // empty, not an error (/info has already confirmed the address is valid).
      if (e.response?.status === 404) {
        setData(shape === "object" ? null : []);
        if (isPaginated && viewAll) setAccumulated([]);
        setError(null);
      } else {
        setError(toError(e));
      }
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, [
    identifier,
    cfg,
    page,
    count,
    order,
    isPaginated,
    viewAll,
    normalizeRows,
    mode.key,
    mode.basePath,
    shape,
    stakeAddress,
  ]);

  useEffect(() => {
    fetchData();
    return () => abortRef.current?.abort();
  }, [fetchData]);

  // Fetch the stake key's authoritative controlled_amount for the "total wallet balance"
  // tile — sums across ALL addresses under the key, not just the current page.
  useEffect(() => {
    if (shape !== "wallet_addresses" || !stakeAddress) {
      setWalletTotal(null);
      return undefined;
    }
    let cancelled = false;
    http
      .get(`${API}/accounts/${stakeAddress}`)
      .then((r) => { if (!cancelled) setWalletTotal(r.data); })
      .catch(() => { if (!cancelled) setWalletTotal(null); });
    return () => { cancelled = true; };
  }, [shape, stakeAddress]);

  // Reset accumulator + page whenever the data scope changes (new identifier,
  // new tab, new count/order, toggled View All).
  useEffect(() => {
    setAccumulated([]);
    setPage(1);
  }, [identifier, tab, count, order, viewAll]);

  const columns = useMemo(
    () => columnsFor(cfg?.colKey || tab, amountMode),
    [tab, cfg, amountMode],
  );

  const pageRows = useMemo(() => normalizeRows(data), [data, normalizeRows]);
  const displayedRows = viewAll && isPaginated ? accumulated : pageRows;
  const startIdx = isPaginated
    ? viewAll
      ? 1
      : (page - 1) * count + 1
    : 0;
  const endIdx = isPaginated ? startIdx + displayedRows.length - 1 : 0;
  // The API returns at most `count` rows. If the most recent fetch was full,
  // there's potentially more.
  const hasMore = pageRows.length === count;

  return (
    <div data-testid={`panel-${tab}`} className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-sm uppercase tracking-[0.2em] text-muted-foreground font-sans">
            {cfg?.label}
          </h3>
          {loading ? <CircleNotch size={14} className="animate-spin text-muted-foreground" /> : null}
        </div>
        <div className="inline-flex border border-border/70 bg-card">
          <button
            data-testid={`${tab}-view-table`}
            onClick={() => setView("table")}
            className={cn(
              "px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] inline-flex items-center gap-1.5 transition-colors",
              view === "table" ? "bg-muted/30 text-foreground" : "text-muted-foreground hover:text-foreground",
            )}
          >
            <TableIcon size={11} /> table
          </button>
          <button
            data-testid={`${tab}-view-json`}
            onClick={() => setView("json")}
            className={cn(
              "px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] inline-flex items-center gap-1.5 border-l border-border/70 transition-colors",
              view === "json" ? "bg-muted/30 text-foreground" : "text-muted-foreground hover:text-foreground",
            )}
          >
            <Code size={11} /> json
          </button>
        </div>
      </div>

      {error ? (
        <div className="border border-red-500/40 bg-red-500/5 text-red-400 p-4 flex items-center justify-between gap-3 flex-wrap">
          <span className="mono text-[12px] inline-flex items-center gap-2">
            <Warning size={14} />
            {(() => {
              const sc = error.status_code;
              const msg = error.message || "";
              if (error._transient) {
                return "The data provider is busy or timed out on this request. Please retry in a moment.";
              }
              return <>{sc ? `${sc} ` : ""}{error.error ? `${error.error}: ` : ""}{msg || "Request failed"}</>;
            })()}
          </span>
          <button
            data-testid={`${tab}-retry`}
            onClick={fetchData}
            disabled={loading}
            className="shrink-0 px-3 py-1.5 border border-red-500/40 bg-red-500/10 text-red-300 text-[10px] uppercase tracking-[0.18em] mono rounded hover:bg-red-500/20 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {loading ? <CircleNotch size={11} className="animate-spin" /> : <CaretRight size={11} />} retry
          </button>
        </div>
      ) : null}

      {!error && isPaginated && !(shape === "wallet_addresses" && !stakeAddress) ? (
        <>
          {shape === "wallet_addresses" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Tile
                testid="wallet-total-balance"
                label="Total Wallet Balance"
                value={
                  walletTotal ? (
                    formatLovelace(walletTotal.controlled_amount, amountMode)
                  ) : (
                    <CircleNotch size={12} className="animate-spin text-muted-foreground inline-block" />
                  )
                }
                hint="Sum across every address under this stake key"
              />
              <Tile
                testid="wallet-address-count"
                label="Addresses Shown"
                value={
                  loading && displayedRows.length === 0 ? (
                    <CircleNotch size={12} className="animate-spin text-muted-foreground inline-block" />
                  ) : (
                    displayedRows.length
                  )
                }
              />
            </div>
          ) : null}
          <Controls
            page={page}
            setPage={setPage}
            count={count}
            setCount={setCount}
            countOptions={isPoolHistory ? [5, 10] : undefined}
            order={order}
            setOrder={setOrder}
            viewAll={viewAll}
            setViewAll={setViewAll}
            startIdx={startIdx}
            endIdx={endIdx}
            resultCount={displayedRows.length}
            pageRowCount={pageRows.length}
            testidPrefix={tab}
          />
          {view === "table" ? (
            shape === "address_transactions" ? (
              <AddressTransactionsList
                rows={displayedRows}
                amountMode={amountMode}
                onPickTx={onPickTx}
                onPickBlock={onPickBlock}
                loading={loading && displayedRows.length === 0}
              />
            ) : shape === "hashes" ? (
              <TxHashesList rows={displayedRows} loading={loading && displayedRows.length === 0} amountMode={amountMode} address={mode.key === "addresses" ? identifier : null} />
            ) : cfg?.colKey === "block_list" ? (
              <BlockListCards rows={displayedRows} amountMode={amountMode} onPickBlock={onPickBlock} loading={loading && displayedRows.length === 0} />
            ) : cfg?.colKey === "pool_history" ? (
              <PoolHistoryTable rows={displayedRows} amountMode={amountMode} loading={loading && displayedRows.length === 0} />
            ) : (
              <Table columns={columns} rows={displayedRows} loading={loading && displayedRows.length === 0} skeletonRows={Math.min(count, 8)} />
            )
          ) : (
            <JsonView data={viewAll ? displayedRows : (data ?? [])} />
          )}
          {viewAll && view === "table" ? (
            <div className="flex justify-center pt-2">
              <button
                data-testid={`${tab}-load-more`}
                onClick={() => setPage((p) => p + 1)}
                disabled={loading || !hasMore}
                className="px-6 py-2 border border-border/70 bg-card text-[11px] uppercase tracking-[0.2em] hover:bg-muted/30 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2 transition-colors"
              >
                {loading ? (
                  <><CircleNotch size={12} className="animate-spin" /> loading…</>
                ) : hasMore ? (
                  <>load more <CaretRight size={11} /></>
                ) : (
                  "end of list"
                )}
              </button>
            </div>
          ) : null}
        </>
      ) : null}

      {!error && shape === "wallet_addresses" && !stakeAddress ? (
        <div data-testid="wallet-no-stake-key" className="text-muted-foreground mono text-[13px] border border-primary/15 bg-card/40 rounded-lg p-5">
          This address is not associated with a stake key, so there are no other addresses to
          group it with under the same wallet.
        </div>
      ) : null}

      {!error && shape === "total" ? (
        view === "json" ? (
          <JsonView data={data ?? {}} />
        ) : (
          <div className="space-y-3">
            <AddressTotalSnapshot data={data} amountMode={amountMode} />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Tile
              testid="total-tx-count"
              label="Tx count"
              value={data?.tx_count ?? "—"}
            />
            <Tile
              testid="total-received"
              label="Received (lovelace)"
              value={formatLovelace(
                data?.received_sum?.find((a) => a.unit === "lovelace")?.quantity,
                amountMode,
              )}
              hint={`${data?.received_sum?.length || 0} asset units`}
            />
            <Tile
              testid="total-sent"
              label="Sent (lovelace)"
              value={formatLovelace(
                data?.sent_sum?.find((a) => a.unit === "lovelace")?.quantity,
                amountMode,
              )}
              hint={`${data?.sent_sum?.length || 0} asset units`}
            />
            </div>
          </div>
        )
      ) : null}

      {!error && shape === "object" ? (
        view === "json" ? (
          <JsonView data={data ?? {}} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Tile testid="ext-type" label="Type" value={<Badge tone="info">{data?.type || "—"}</Badge>} />
            <Tile testid="ext-script" label="Script" value={data?.script ? <Badge tone="warn">yes</Badge> : "no"} />
            <Tile
              testid="ext-stake"
              label="Stake address"
              value={
                data?.stake_address ? (
                  <span className="inline-flex items-center gap-2">
                    <Mono>{truncMid(data.stake_address, 12, 8)}</Mono>
                    <CopyBtn value={data.stake_address} label="stake" />
                  </span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )
              }
            />
            {(data?.amount || []).map((a) => (
              <Tile
                key={a.unit}
                testid={`ext-amt-${a.unit.slice(0, 8)}`}
                label={a.unit === "lovelace" ? "Lovelace" : truncMid(a.unit, 14, 6)}
                value={
                  a.unit === "lovelace"
                    ? formatLovelace(a.quantity, amountMode)
                    : Number(a.quantity).toLocaleString("en-US")
                }
                hint={
                  a.decimals !== undefined && a.decimals !== null
                    ? `${a.decimals} decimals`
                    : null
                }
              />
            ))}
          </div>
        )
      ) : null}

      {!error && shape === "tx_utxos" && data ? (
        view === "json" ? (
          <JsonView data={data ?? {}} />
        ) : (
          <FromToList inputs={data.inputs || []} outputs={data.outputs || []} />
        )
      ) : null}

      {!error && shape === "tx_flow" && data ? (
        view === "json" ? (
          <JsonView data={data ?? {}} />
        ) : (
          <TxFlowDiagram inputs={data.inputs || []} outputs={data.outputs || []} />
        )
      ) : null}
    </div>
  );
};
